import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, UserModalStyles } from "./styles/adminUsers.js";
import {
  ADMIN_USERS_ENDPOINT,
  ADMIN_USERS_UPDATE_ENDPOINT,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  username: "",
  phone: "",
  roles: "",
  password: "",
};

const AdminUsers = () => {
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    targetUser: null,
    form: initialForm,
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");

  const isAdmin = useMemo(
    () =>
      Array.isArray(user?.roles) &&
      user.roles.some(
        (role) => typeof role === "string" && role.toLowerCase() === "admin"
      ),
    [user]
  );

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }

      const response = await fetch(
        `${ADMIN_USERS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load users.");
      }

      const payload = await response.json();
      setUsers(payload.users || []);
      if (payload.pagination) {
        const serverPage = payload.pagination.page || page;
        setPagination({
          page: serverPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (serverPage !== page) {
          setPage(serverPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: payload.users?.length || 0,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load users.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchUsers();
    }
  }, [authLoading, isAdmin, fetchUsers]);

  const tableRows = useMemo(
    () =>
      users.map((item) => ({
        id: item.id,
        name:
          `${item.firstName || ""} ${item.lastName || ""}`.trim() ||
          item.username,
        email: item.email,
        username: item.username,
        phone: item.phone || "—",
        roles: Array.isArray(item.roles) ? item.roles : [],
        raw: item,
      })),
    [users]
  );

  const openModal = (mode, targetUser = null) => {
    if (mode === "edit" && targetUser) {
      setModalState({
        show: true,
        mode,
        targetUser,
        form: {
          firstName: targetUser.firstName || "",
          lastName: targetUser.lastName || "",
          email: targetUser.email || "",
          username: targetUser.username || "",
          phone: targetUser.phone || "",
          roles: Array.isArray(targetUser.roles)
            ? targetUser.roles.join(", ")
            : "",
          password: "",
        },
        saving: false,
        error: "",
      });
    } else {
      setModalState({
        show: true,
        mode: "add",
        targetUser: null,
        form: initialForm,
        saving: false,
        error: "",
      });
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      targetUser: null,
      form: initialForm,
      saving: false,
      error: "",
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      if (
        !modalState.form.firstName.trim() ||
        !modalState.form.lastName.trim() ||
        !modalState.form.email.trim() ||
        !modalState.form.username.trim()
      ) {
        throw new Error(
          "First name, last name, email, and username are required."
        );
      }

      if (modalState.mode === "add" && !modalState.form.password.trim()) {
        throw new Error("Password is required when creating a user.");
      }

      const payload = {
        firstName: modalState.form.firstName.trim(),
        lastName: modalState.form.lastName.trim(),
        email: modalState.form.email.trim(),
        username: modalState.form.username.trim(),
        phone: modalState.form.phone.trim(),
        roles: modalState.form.roles
          ? modalState.form.roles
              .split(",")
              .map((role) => role.trim())
              .filter(Boolean)
          : [],
      };

      if (modalState.form.password.trim()) {
        payload.password = modalState.form.password.trim();
      }

      const requestOptions = {
        method: modalState.mode === "add" ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "add"
          ? ADMIN_USERS_ENDPOINT
          : ADMIN_USERS_UPDATE_ENDPOINT(modalState.targetUser.id);

      const response = await fetch(endpoint, requestOptions);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Unable to save user.");
      }

      await response.json();
      closeModal();
      fetchUsers();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save user.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }
    setDeletingId(id);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_USERS_UPDATE_ENDPOINT(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to delete user.");
      }

      fetchUsers();
    } catch (err) {
      setError(err.message || "Unable to delete user.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Styles>
      <UserModalStyles />
      <div className="main-wrapper admin-users-page">
        <HeaderTwo />
        <BreadcrumbBox title="Manage Users" />

        <section className="users-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>Add User</Button>
                      <div className="filters">
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={searchInput}
                          onChange={(event) =>
                            setSearchInput(event.target.value)
                          }
                          disabled={loading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setSearchInput("");
                            setSearchTerm("");
                            setPage(1);
                          }}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="success"
                          onClick={() => {
                            setSearchTerm(searchInput.trim());
                            setPage(1);
                          }}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}
                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        {tableRows.length === 0 ? (
                          <Alert variant="info" className="mb-0">
                            No users found.
                          </Alert>
                        ) : (
                          <>
                            <table>
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Email</th>
                                  <th>Username</th>
                                  <th>Phone</th>
                                  <th>Roles</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{row.name}</td>
                                    <td>{row.email}</td>
                                    <td>{row.username}</td>
                                    <td>{row.phone}</td>
                                    <td>
                                      {row.roles.length > 0 ? (
                                        row.roles.join(", ")
                                      ) : (
                                        <span className="muted">None</span>
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() =>
                                          openModal("edit", row.raw)
                                        }
                                      >
                                        Edit
                                      </button>
                                      <span> | </span>
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() => deleteUser(row.id)}
                                        disabled={deletingId === row.id}
                                      >
                                        {deletingId === row.id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="pagination-controls">
                              <div className="page-info">
                                Page {pagination.page} of{" "}
                                {pagination.totalPages} ·{" "}
                                {pagination.totalItems} total
                              </div>
                              <div className="page-actions">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() =>
                                    setPage((prev) => Math.max(prev - 1, 1))
                                  }
                                  disabled={pagination.page <= 1 || loading}
                                >
                                  Previous
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  onClick={() =>
                                    setPage((prev) =>
                                      Math.min(prev + 1, pagination.totalPages)
                                    )
                                  }
                                  disabled={
                                    pagination.page >= pagination.totalPages ||
                                    loading
                                  }
                                >
                                  Next
                                </Button>
                                <select
                                  value={pageSize}
                                  onChange={(event) => {
                                    const newSize =
                                      Number(event.target.value) || 10;
                                    setPageSize(newSize);
                                    setPage(1);
                                  }}
                                  disabled={loading}
                                >
                                  {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                      {size} / page
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        dialogClassName="admin-users-modal"
        size="lg"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add User" : "Edit User"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger">{modalState.error}</Alert>
            )}
            <Row>
              <Col md="6">
                <Form.Group controlId="user_firstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={modalState.form.firstName}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="user_lastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={modalState.form.lastName}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="user_email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={modalState.form.email}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="user_username">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={modalState.form.username}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="user_phone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={modalState.form.phone}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="user_roles">
                  <Form.Label>Roles (comma separated)</Form.Label>
                  <Form.Control
                    type="text"
                    name="roles"
                    value={modalState.form.roles}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="12">
                <Form.Group controlId="user_password">
                  <Form.Label>
                    Password{" "}
                    {modalState.mode === "edit" && (
                      <span className="text-muted">
                        (leave blank to keep existing)
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={modalState.form.password}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder={modalState.mode === "edit" ? "••••••••" : ""}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={modalState.saving}
            >
              {modalState.saving ? "Saving..." : "Save User"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminUsers;
